<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import AM3Collapsible from '@/components/am3-generic/layout/AM3Collapsible.vue'
import AM3InputDate from '@/components/am3-generic/inputs/entry/AM3InputDate.vue'

interface Props {
  startDate?: string | null
  endDate?: string | null
  minDate?: string
  maxDate?: string
}

const props = withDefaults(defineProps<Props>(), {
  startDate: null,
  endDate: null,
})

interface Emits {
  (e: 'update:startDate', value: string | null): void
  (e: 'update:endDate', value: string | null): void
  (e: 'apply', value: { startDate: string | null; endDate: string | null }): void
  (e: 'clear'): void
}

const emit = defineEmits<Emits>()

const internalStartDate = ref<string | null>(props.startDate)
const internalEndDate = ref<string | null>(props.endDate)

watch(() => props.startDate, (val) => { internalStartDate.value = val })
watch(() => props.endDate, (val) => { internalEndDate.value = val })

const hasDateFilter = computed(() => !!(internalStartDate.value || internalEndDate.value))

function handleApply() {
  emit('update:startDate', internalStartDate.value)
  emit('update:endDate', internalEndDate.value)
  emit('apply', { startDate: internalStartDate.value, endDate: internalEndDate.value })
}

function handleClear() {
  internalStartDate.value = null
  internalEndDate.value = null
  emit('update:startDate', null)
  emit('update:endDate', null)
  emit('clear')
}
</script>

<template>
  <div
    data-test-id="am3-date-range-picker"
    class="am3-date-range-filter py-2 px-4 bg-surface flex flex-col items-start w-full"
  >
    <AM3Collapsible
      title="Date range"
      :default-collapsed="!hasDateFilter"
    >
      <div class="flex flex-col gap-2 items-stretch">
        <div class="flex gap-2">
          <AM3InputDate
            v-model="internalStartDate"
            label="From"
            placeholder="Start date"
            :min-date="minDate"
            :max-date="internalEndDate || maxDate"
            :stay-open-on-select="false"
            data-test-id="date-range-filter-start"
          />
          <AM3InputDate
            v-model="internalEndDate"
            label="To"
            placeholder="End date"
            :min-date="internalStartDate || minDate"
            :max-date="maxDate"
            :stay-open-on-select="false"
            data-test-id="date-range-filter-end"
          />
        </div>
        <div class="flex flex-row-reverse [&>button]:w-full gap-2 mb-2">
          <AM3Button
            text="Apply"
            type="primary"
            size="sm"
            :disabled="!internalStartDate && !internalEndDate"
            data-test-id="date-range-filter-apply"
            @click="handleApply"
          />
          <AM3Button
            text="Clear"
            type="tertiary"
            size="sm"
            :disabled="!hasDateFilter"
            data-test-id="date-range-filter-clear"
            @click="handleClear"
          />
        </div>
      </div>
    </AM3Collapsible>
  </div>
</template>
